import { api } from "./api";

// ===============================================================
// Master tahap produksi — Batch 2.2.
//
// Sumber tunggal daftar tahap untuk seluruh frontend. Menggantikan DUA
// konstanta yang dulu tersalin (STAGE_ORDER/STAGE_LABEL di
// rejectRemakeApi.js dan STAGE_LABEL di WODetail.jsx) — keduanya harus
// disunting tiap kali tahap berubah, dan sempat menyimpan 5 tahap lama.
//
// Menambah/mengubah tahap = mengisi data master di backend; frontend ikut
// TANPA perubahan kode dan tanpa deploy ulang.
//
// TIDAK ADA DAFTAR TAHAP CADANGAN DI SINI. Kalau master tidak terbaca,
// UI menampilkan keadaan kosong + pesan galat — bukan diam-diam memakai
// daftar bawaan yang mungkin sudah tidak benar. Daftar bawaan adalah
// persis jenis hardcode yang batch ini hapus.
// ===============================================================

let _cache = null;
let _inflight = null;

/** Ambil tahap aktif (terurut sequence). Di-cache selama sesi halaman. */
export async function fetchProductionStages({ force = false } = {}) {
  if (_cache && !force) return _cache;
  if (_inflight && !force) return _inflight;
  _inflight = api
    .get("/production-stages")
    .then((r) => {
      _cache = r.data || [];
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

export function clearProductionStagesCache() {
  _cache = null;
  _inflight = null;
}

/** {code: name} dari daftar tahap. */
export function stageLabelMap(stages) {
  return (stages || []).reduce((acc, s) => {
    acc[s.code] = s.name;
    return acc;
  }, {});
}

/** Label satu tahap; fallback ke kode mentah supaya WO lama yang memakai
 *  tahap non-aktif tetap terbaca (bukan tampil kosong). */
export function stageLabel(stages, code) {
  return stageLabelMap(stages)[code] || code;
}

/** Tahap yang akan ditandai SKIPPED saat WO dikunci — cermin persis
 *  `wo_service._lewati_tahap_sebelum_tahap_terakhir_lama` (Q45):
 *  hanya bila SEMUA FA sumber `is_order_lama`; batas = tahap terakhir yang
 *  PALING AWAL di antara sumber; `GUDANG_JADI` = seluruh tahap kerja;
 *  tahap yang nol dikenal master → nol ada yang dilewati. Campuran FA lama +
 *  FA biasa → [] (FA baru wajib mulai dari Layout).
 *  Murni: nol jaringan, nol state. `stages` = master aktif terurut sequence
 *  (termasuk GUDANG_JADI, yang bukan pekerjaan dan nol pernah "dilewati").
 *  WO-KUNCI-FA-LAMA-DIALOG-NOL-MENYEBUT-TAHAP-DILEWATI-01 (vonis 15 Sep 2026). */
export function tahapDilewatiFaLama(sourceFas, stages) {
  const fas = sourceFas || [];
  if (!fas.length || !fas.every((f) => f && f.is_order_lama)) return [];
  const kerja = (stages || []).filter((s) => s.code !== "GUDANG_JADI");
  if (!kerja.length) return [];
  const urutan = Object.fromEntries(kerja.map((s) => [s.code, s.sequence]));
  const maxSeq = Math.max(...kerja.map((s) => s.sequence));
  const batas = [];
  for (const f of fas) {
    const kode = f.tahap_terakhir_lama;
    if (kode === "GUDANG_JADI") batas.push(maxSeq + 1);
    else if (kode in urutan) batas.push(urutan[kode]);
    else return [];
  }
  const seqBatas = Math.min(...batas);
  return kerja.filter((s) => s.sequence < seqBatas);
}

/** Tahap yang NOL PERNAH punya serah terima borongan berkuantitas — constraint
 *  DB Q22 (`stage_handovers`) menolak tahap 1–2 sebagai asal. SATU sumber
 *  untuk layar serah terima (nol menawarkan keduanya) DAN pagar pengingat
 *  tutup tahap di WODetail (nol bertanya "sisa" untuk tahap yang nol bisa
 *  diserahterimakan — PAGAR-TUTUP-TAHAP-MENGINGATKAN-LAYOUT-PRINT-01). */
export const TAHAP_TANPA_SERAH_TERIMA = ["LAYOUT", "PRINT_SUBLIM"];
